import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Grid,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface AvailableDate {
    id: string;
    date: string;
}

const AdminDates: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
    const [error, setError] = useState('');
    const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
    const [adminCode, setAdminCode] = useState('');
    const navigate = useNavigate();

    // Charger les dates disponibles au démarrage
    useEffect(() => {
        const savedDates = localStorage.getItem('availableDates');
        if (savedDates) {
            setAvailableDates(JSON.parse(savedDates));
        }
    }, []);

    // Sauvegarder les dates disponibles
    const saveAvailableDates = (dates: AvailableDate[]) => {
        localStorage.setItem('availableDates', JSON.stringify(dates));
    };

    const handleAddDate = () => {
        if (!selectedDate) {
            setError('Veuillez sélectionner une date');
            return;
        }

        // Corriger le décalage de date en utilisant la date locale
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Vérifier si la date existe déjà
        if (availableDates.some(date => {
            const existingDate = new Date(date.date);
            const newDate = new Date(dateStr);
            return existingDate.getTime() === newDate.getTime();
        })) {
            setError('Cette date est déjà disponible');
            return;
        }

        const newDate: AvailableDate = {
            id: Date.now().toString(),
            date: dateStr
        };

        const updatedDates = [...availableDates, newDate];
        setAvailableDates(updatedDates);
        saveAvailableDates(updatedDates);
        setSelectedDate(null);
        setError('');
    };

    const handleDeleteDate = (id: string) => {
        const updatedDates = availableDates.filter(date => date.id !== id);
        setAvailableDates(updatedDates);
        saveAvailableDates(updatedDates);
    };

    const handleDeleteAllDates = () => {
        if (adminCode === 'maeva2') {
            setAvailableDates([]);
            saveAvailableDates([]);
            setOpenDeleteAllDialog(false);
            setAdminCode('');
        } else {
            setError('Code administrateur incorrect');
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h1">
                        Gestion des Dates
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/admin')}
                    >
                        Retour
                    </Button>
                </Box>

                <Typography variant="h4" component="h1" gutterBottom>
                    Gestion des Dates Disponibles
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Ajouter une date disponible
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                                <DatePicker
                                    label="Date"
                                    value={selectedDate}
                                    onChange={(newValue) => {
                                        setSelectedDate(newValue);
                                        setError('');
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddDate}
                                fullWidth
                            >
                                Ajouter
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Dates disponibles
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setOpenDeleteAllDialog(true)}
                    >
                        Supprimer toutes les dates
                    </Button>
                </Box>

                <List>
                    {availableDates.map((date) => (
                        <ListItem
                            key={date.id}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    aria-label="supprimer"
                                    onClick={() => handleDeleteDate(date.id)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={format(new Date(date.date), 'EEEE d MMMM yyyy', { locale: fr })}
                            />
                        </ListItem>
                    ))}
                </List>

                <Dialog open={openDeleteAllDialog} onClose={() => setOpenDeleteAllDialog(false)}>
                    <DialogTitle>Supprimer toutes les dates</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Code administrateur"
                            type="password"
                            fullWidth
                            value={adminCode}
                            onChange={(e) => setAdminCode(e.target.value)}
                            error={!!error}
                            helperText={error}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setOpenDeleteAllDialog(false);
                            setAdminCode('');
                            setError('');
                        }}>
                            Annuler
                        </Button>
                        <Button onClick={handleDeleteAllDates} color="error" variant="contained">
                            Supprimer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default AdminDates;